import { Link } from "react-router-dom";
import { Sun, PlugZap, ClipboardCheck, Wrench, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader, Reveal, SectionHeading } from "@/components/bits";
import { COMPANY, IMAGES } from "@/data/site";

const WHAT_WE_DO = [
  {
    icon: Sun,
    title: "Solar Product Supply",
    text: "Solar panels, inverters, batteries, mounting structures, and related components supplied according to project requirements and availability.",
  },
  {
    icon: PlugZap,
    title: "Electrical Component Supply",
    text: "Wires and cables, connectors, lugs, terminals, and other electrical and industrial products.",
  },
  {
    icon: ClipboardCheck,
    title: "Site Survey & Planning",
    text: "Site assessment and system planning support to help determine the right configuration and components.",
  },
  {
    icon: Wrench,
    title: "Installation Support",
    text: "Installation and project support arranged according to the agreed scope, with coordination during execution.",
  },
];

const About = () => (
  <>
    <Seo
      title="About Us | Ambrotech Private Limited"
      description="Ambrotech Private Limited supplies solar energy products, electrical components, and related equipment for residential, commercial, and industrial requirements."
      path="/about"
    />

    <PageHeader
      eyebrow="About Us"
      title="A dependable supply partner for solar and electrical needs."
      lead={COMPANY.intro}
    />

    <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
              What Ambrotech does
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              Ambrotech Private Limited is a supplier of solar energy products, electrical
              components, and related equipment. We serve residential, commercial, and industrial
              customers with products sourced for their projects.
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Our range covers solar PV modules, on-grid and hybrid inverters, single-phase and
              three-phase inverters, lithium batteries and energy storage, mounting structures,
              DC and AC wires and cables, connectors, balance-of-system components, and other
              solar, electrical, and industrial products.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Products are supplied according to project requirements and availability. Alongside
              supply, we provide site surveys, system planning, and installation support arranged
              according to the agreed scope.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <p className="mt-6 font-display text-lg font-semibold text-navy">{COMPANY.tagline}</p>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="relative">
            <div aria-hidden="true" className="absolute -left-3 -top-3 hidden h-full w-full rounded-2xl border border-brand/50 sm:block" />
            <img
              src={IMAGES.solarField}
              alt="Solar panel array installed across an open field"
              loading="lazy"
              className="relative h-[320px] w-full rounded-2xl object-cover shadow-[0_24px_60px_rgba(11,25,44,0.15)] sm:h-[420px]"
              data-testid="about-image"
            />
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-slate-50 py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading eyebrow="Our Activities" title="Solar, electrical, and project supply" />
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {WHAT_WE_DO.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.06} className="h-full">
              <div className="flex h-full gap-5 rounded-2xl border border-slate-200 bg-white p-7" data-testid={`about-activity-${i}`}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy text-brand">
                  <w.icon className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-semibold text-navy">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
      <Reveal>
        <div className="flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-navy px-8 py-12 sm:px-12">
          <div>
            <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">
              Looking for a product or a project quote?
            </h2>
            <p className="mt-2 text-sm text-slate-300 sm:text-base">
              Send us your requirement and we will respond with availability.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="about-contact-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover"
          >
            Contact Us
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        </div>
      </Reveal>
    </section>
  </>
);

export default About;